import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import { nationalityData } from '../../../../../utils/nationality_data';
import './country_perform.css';

// # Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

// # Country Performance Component
const CountryPerform = () => {
  // # State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countryData, setCountryData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRegion, setFilterRegion] = useState('All');
  const [sortBy, setSortBy] = useState('users');
  const [sortOrder, setSortOrder] = useState('desc');

  // # Fetch data on component mount
  useEffect(() => {
    fetchCountryData();
  }, []);

  // # Fetch all required data
  const fetchCountryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // # Get collections references
      const usersRef = collection(db, 'users');
      const subscriptionsRef = collection(db, 'subscriptions');
      const financialRef = collection(db, 'financial');

      // # Fetch all data
      const [usersSnap, subscriptionsSnap, financialSnap] = await Promise.all([
        getDocs(usersRef),
        getDocs(subscriptionsRef),
        getDocs(financialRef)
      ]);

      // # Process data by country
      const processedData = {};

      // # Process users
      usersSnap.forEach(doc => {
        const userData = doc.data();
        const country = userData.nationality || 'Unknown';
        
        if (!processedData[country]) {
          processedData[country] = {
            users: 0,
            activeSubscriptions: 0,
            payments: 0,
            revenue: 0
          };
        }
        processedData[country].users++;
      });

      // # Process subscriptions
      subscriptionsSnap.forEach(doc => {
        const subData = doc.data();
        if (subData.status === 'active') {
          const userDoc = usersSnap.docs.find(d => d.id === subData.userId);
          if (userDoc) {
            const country = userDoc.data().nationality || 'Unknown';
            if (processedData[country]) {
              processedData[country].activeSubscriptions++;
            }
          }
        }
      });

      // # Process payments from financial collection
      financialSnap.forEach(doc => {
        const financialData = doc.data();
        
        // # Process payments in each document
        if (financialData.payments) {
          Object.entries(financialData.payments).forEach(([paymentId, payment]) => {
            const userData = payment.user;
            const payData = payment.pay;
            
            if (userData && payData && payData.egp_total) {
              const country = userData.nationality || 'Unknown';
              const amount = Number(payData.egp_total) || 0;
              
              if (processedData[country]) {
                processedData[country].payments++;
                processedData[country].revenue += amount;
              }
            }
          });
        }
      });

      setCountryData(processedData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching country data:', err);
      setError('Failed to load country performance data');
      setLoading(false);
    }
  };

  // # Get region options
  const getRegions = () => {
    const regions = new Set();
    Object.entries(nationalityData).forEach(([_, data]) => {
      data.searchKeys.forEach(key => {
        if (['arab', 'asia', 'europe', 'americas', 'africa', 'oceania'].includes(key)) {
          regions.add(key.charAt(0).toUpperCase() + key.slice(1));
        }
      });
    });
    return ['All', ...Array.from(regions).sort()];
  };

  // # Filter and sort data
  const getFilteredAndSortedData = () => {
    return Object.entries(countryData)
      .filter(([country]) => {
        const countryInfo = Object.entries(nationalityData).find(([_, data]) => 
          data.name.toLowerCase() === country.toLowerCase()
        );
        
        // # Apply search filter
        const matchesSearch = !searchQuery || 
          country.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (countryInfo && countryInfo[1].name.toLowerCase().includes(searchQuery.toLowerCase()));

        // # Apply region filter
        const matchesRegion = filterRegion === 'All' || 
          (countryInfo && countryInfo[1].searchKeys.includes(filterRegion.toLowerCase()));

        return matchesSearch && matchesRegion;
      })
      .sort(([countryA, dataA], [countryB, dataB]) => {
        const multiplier = sortOrder === 'desc' ? -1 : 1;
        return multiplier * (dataA[sortBy] - dataB[sortBy]);
      });
  };

  // # Get country info from nationality data
  const getCountryInfo = (countryName) => {
    return Object.entries(nationalityData).find(([_, data]) => 
      data.name.toLowerCase() === countryName.toLowerCase()
    )?.[1] || { name: countryName, flag: '🌍' };
  };

  if (loading) {
    return <div className="cnt_prf_loading">Loading country performance data...</div>;
  }

  if (error) {
    return <div className="cnt_prf_error">{error}</div>;
  }

  const filteredData = getFilteredAndSortedData();

  return (
    <div className="cnt_prf_container">
      <div className="cnt_prf_header">
        <h2>Country Performance Analytics</h2>
        <p>Analyze user engagement and revenue by country</p>
      </div>

      <div className="cnt_prf_filters">
        {/* Search Input */}
        <input
          type="text"
          className="cnt_prf_search sgnp_cmn_input"
          placeholder="Search countries..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {/* Region Filter */}
        <select
          className="cnt_prf_filter-select sgnp_cmn_input"
          value={filterRegion}
          onChange={(e) => setFilterRegion(e.target.value)}
        >
          {getRegions().map(region => (
            <option key={region} value={region}>{region}</option>
          ))}
        </select>

        {/* Sort Options */}
        <select
          className="cnt_prf_filter-select sgnp_cmn_input"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="users">Sort by Users</option>
          <option value="activeSubscriptions">Sort by Active Subscriptions</option>
          <option value="payments">Sort by Payments</option>
          <option value="revenue">Sort by Revenue</option>
        </select>

        <select
          className="cnt_prf_filter-select sgnp_cmn_input"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        >
          <option value="desc">Highest First</option>
          <option value="asc">Lowest First</option>
        </select>
      </div>

      {filteredData.length === 0 ? (
        <div className="cnt_prf_empty">No countries match your search criteria</div>
      ) : (
        <div className="cnt_prf_grid">
          {filteredData.map(([country, data]) => {
            const countryInfo = getCountryInfo(country);
            return (
              <div key={country} className="cnt_prf_card">
                <div className="cnt_prf_card-header">
                  <span className="cnt_prf_flag">{countryInfo.flag}</span>
                  <span className="cnt_prf_country-name">{countryInfo.name}</span>
                </div>
                <div className="cnt_prf_stats">
                  <div className="cnt_prf_stat-item">
                    <div className="cnt_prf_stat-label">Users</div>
                    <div className="cnt_prf_stat-value">{data.users}</div>
                  </div>
                  <div className="cnt_prf_stat-item">
                    <div className="cnt_prf_stat-label">Active Subs</div>
                    <div className="cnt_prf_stat-value">{data.activeSubscriptions}</div>
                  </div>
                  <div className="cnt_prf_stat-item">
                    <div className="cnt_prf_stat-label">Payments</div>
                    <div className="cnt_prf_stat-value">{data.payments}</div>
                  </div>
                  <div className="cnt_prf_stat-item cnt_prf_revenue">
                    <div className="cnt_prf_stat-label">Total Revenue - EGP</div>
                    <div className="cnt_prf_stat-value">{formatCurrency(data.revenue)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CountryPerform;
